import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { acceptFounderAgreement, logout } from '@/lib/api/auth';
import {
  clearFounderAgreementGateState,
  DEFAULT_FOUNDER_AGREEMENT_MESSAGE,
  readFounderAgreementGateState,
} from '@/lib/founderAgreementGate';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import foundingCircleAgreementHtml from '@/content/legal/founding-circle-agreement.html?raw';
import legalDocumentStyles from '@/pages/legal/LegalDocumentPage.module.css';

const extractBodyHtml = (html: string) => {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match?.[1] ?? html;
};

interface FounderAgreementModalProps {
  open: boolean;
  onAccepted: () => void;
}

export const FounderAgreementModal: React.FC<FounderAgreementModalProps> = ({
  open,
  onAccepted,
}) => {
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const gateState = readFounderAgreementGateState();
  const founderAgreement = gateState?.founderAgreement;

  const versionLabel = founderAgreement?.agreementVersion || 'founding-circle-v1';
  const agreementBodyHtml = useMemo(
    () => extractBodyHtml(foundingCircleAgreementHtml),
    []
  );
  const acceptedAtLabel = useMemo(() => {
    if (!founderAgreement?.acceptedAt) {
      return null;
    }

    return format(new Date(founderAgreement.acceptedAt), 'd MMM yyyy');
  }, [founderAgreement?.acceptedAt]);

  const handleAccept = async () => {
    if (!accepted) {
      showWarningToast(
        'Agreement required',
        'Please confirm that you accept the Founding Circle agreement.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await acceptFounderAgreement({
        accepted: true,
        agreementVersion: versionLabel,
      });

      if (response.founderAgreement.accessBlocked) {
        showWarningToast(
          'Agreement still required',
          'Your agreement could not be finalized yet. Please try again.'
        );
        return;
      }

      clearFounderAgreementGateState();
      setAccepted(false);
      showSuccessToast('Agreement accepted', 'Thanks. Your Founding Circle access is now active.');
      onAccepted();
    } catch (error) {
      showErrorToast(error, 'Could not save agreement');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="!flex h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[72rem] flex-col gap-3 overflow-hidden p-4 sm:h-[92dvh] sm:w-[92vw] sm:gap-4 sm:p-6 lg:w-[82vw] xl:w-[72rem]"
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0 pr-8">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#0f7fa4]">
            Founding Circle
          </p>
          <DialogTitle className="text-xl font-semibold text-slate-800 sm:text-2xl">
            Founding Circle agreement
          </DialogTitle>
          <DialogDescription className="text-sm leading-6 text-slate-600">
            {gateState?.message || DEFAULT_FOUNDER_AGREEMENT_MESSAGE}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1 rounded-md border border-slate-200 bg-white">
          <div
            className={`${legalDocumentStyles.documentContent} ${legalDocumentStyles.documentContentFull}`}
            dangerouslySetInnerHTML={{ __html: agreementBodyHtml }}
          />
          <div className="border-t border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-700">
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <p className="font-medium text-slate-700">Agreement version</p>
              <p className="mt-1 text-slate-900">{versionLabel}</p>
              {acceptedAtLabel ? (
                <>
                  <p className="mt-3 font-medium text-slate-700">Accepted on</p>
                  <p className="mt-1 text-slate-900">{acceptedAtLabel}</p>
                </>
              ) : null}
            </div>
          </div>
        </ScrollArea>

        <label className="flex shrink-0 items-start gap-3 text-sm leading-6 text-slate-700">
          <input
            type="checkbox"
            checked={accepted}
            disabled={isSubmitting}
            onChange={(event) => setAccepted(event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-[#20AEDC] focus:ring-[#20AEDC]"
          />
          <span>
            I have read and accept the Founding Circle agreement for my CalorieScience account.
          </span>
        </label>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => logout()}
            disabled={isSubmitting}
          >
            Logout
          </Button>
          {accepted ? (
            <Button
              type="button"
              onClick={handleAccept}
              disabled={isSubmitting}
              className="!bg-[#20AEDC] !text-white hover:!bg-[#1a99c0]"
            >
              {isSubmitting ? 'Saving...' : 'Accept and continue'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
